import {
  CreateTransactionRuleModal,
  TransactionsManager,
} from '@carboncopyco/editor-ui';
import {
  getNormlizedMockResponse,
  isMockResponseModifier,
} from '@carboncopyco/editor-utils';
import type { TransactionModifier } from '@carboncopyco/types';
import { useBoolean } from 'ahooks';
import { memo, useMemo, useState } from 'react';
import {
  useEditor,
  useEditorTransactionModifications,
  useSpreadsheetApi,
} from '../context/EditorSdkProvider';
import { useEditorEvent } from '../hooks/useEditorEvent';
import { useEditorTool } from '../hooks/useEditorTool';

export const TransactionsManagerTool = memo(() => {
  const editorSdk = useEditor();
  const { filesServiceDomain, openUrlLink } = editorSdk;
  const { isOpen, open, close } = useEditorTool('transactions');
  const {
    transactions,
    transactionModifications,
    createTransactionRule,
    editTransactionModification,
    deleteTransactionModification,
    reorderTransactionModification,
  } = useEditorTransactionModifications();
  const spreadsheetApi = useSpreadsheetApi();
  const [isCreationModalOpen, setIsCreationModalOpen] = useBoolean();
  const [transactionsForElement, setTransactionsForElement] =
    useState(undefined);
  const [isVisualSearch, setIsVisualSearch] = useBoolean();

  const transformModifiers = (modifiers: TransactionModifier[]) =>
    modifiers.map((modifier) =>
      isMockResponseModifier(modifier)
        ? getNormlizedMockResponse(modifier, {
            filesServiceDomain,
          })
        : modifier
    );

  const modifiedTransactionRules = useMemo(
    () =>
      transactionModifications.map((modification) => ({
        ...modification,
        content: {
          ...modification.content,
          rules: modification.content.rules.map((rule) => ({
            ...rule,
            modifiers: transformModifiers(rule.modifiers),
          })),
        },
      })),
    [transactionModifications]
  );

  useEditorEvent(editorSdk, 'openDataRuleCreation', (transactions) => {
    setTransactionsForElement(transactions);
    open();
    setIsCreationModalOpen.setTrue();
    setIsVisualSearch.setTrue();
  });

  const cancelTransactionsForElement = () => {
    setTransactionsForElement(undefined);
    setIsVisualSearch.setFalse();
  };

  const closeCreationModal = () => {
    setIsCreationModalOpen.setFalse();
    cancelTransactionsForElement();
  };

  return (
    <>
      <TransactionsManager
        isOpen={isOpen}
        onClose={!isCreationModalOpen ? close : undefined}
        transactionModifications={modifiedTransactionRules}
        createTransactionRule={createTransactionRule}
        editTransactionModification={editTransactionModification}
        deleteTransactionModification={deleteTransactionModification}
        reorderTransactionModification={reorderTransactionModification}
        onLearnMoreClick={openUrlLink}
        openCreationModal={setIsCreationModalOpen.setTrue}
        spreadsheetApi={spreadsheetApi}
      />
      <CreateTransactionRuleModal
        open={isCreationModalOpen}
        transactionModifications={transactionModifications}
        onClose={closeCreationModal}
        onLearnMoreClick={openUrlLink}
        transactions={transactionsForElement ?? transactions}
        isVisualSearch={isVisualSearch}
        cancelVisualSearch={cancelTransactionsForElement}
        onSubmit={async (transactionRule, shouldRefresh) => {
          await createTransactionRule(transactionRule, shouldRefresh);
          if (shouldRefresh) {
            closeCreationModal();
          }
        }}
      />
    </>
  );
});

TransactionsManagerTool.displayName = 'TransactionsManagerTool';
